import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LaunchCodeBootstrap } from "@/components/LaunchCodeBootstrap";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import DashboardPage from "@/pages/DashboardPage";
import DirectoryPage from "@/pages/DirectoryPage";
import ProfilePage from "@/pages/ProfilePage";
import NotificationPreferencesPage from "@/pages/NotificationPreferencesPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminInvitesPage from "@/pages/AdminInvitesPage";
import MasterTenantsPage from "@/pages/MasterTenantsPage";
import MasterWorkspaceDetailPage from "@/pages/MasterWorkspaceDetailPage";
import PacePage from "@/pages/pace/PacePage";
import ClockPage from "@/pages/time/ClockPage";
import WeekPage from "@/pages/time/WeekPage";
import HistoryPage from "@/pages/time/HistoryPage";
import WorksitesPage from "@/pages/admin/WorksitesPage";
import WorksiteDetailPage from "@/pages/admin/WorksiteDetailPage";
import DimensionsPage from "@/pages/admin/DimensionsPage";
import DimensionDetailPage from "@/pages/admin/DimensionDetailPage";
import TimePolicyPage from "@/pages/admin/TimePolicyPage";
import AdminTimePage from "@/pages/admin/AdminTimePage";
import AdminApprovalsPage from "@/pages/admin/AdminApprovalsPage";
import PendingQueue from "@/pages/approvals/PendingQueue";
import ApprovalsHistory from "@/pages/approvals/ApprovalsHistory";
import TimesheetDetailPage from "@/pages/approvals/TimesheetDetailPage";
import MagicApprovePage from "@/pages/approvals/MagicApprovePage";
import PtoQueuePage from "@/pages/approvals/PtoQueuePage";
import MyPtoPage from "@/pages/pto/MyPtoPage";
import OvertimePolicyPage from "@/pages/admin/OvertimePolicyPage";
import BreakPolicyPage from "@/pages/admin/BreakPolicyPage";
import PtoTypesPage from "@/pages/admin/PtoTypesPage";
import PtoRequestsPage from "@/pages/admin/PtoRequestsPage";
import ChecklistsPage from "@/pages/admin/ChecklistsPage";
import KiosksPage from "@/pages/admin/KiosksPage";
import PrivacyNoticePage from "@/pages/admin/PrivacyNoticePage";
import ApiKeysPage from "@/pages/admin/ApiKeysPage";
import ApiKeyUsagePage from "@/pages/admin/ApiKeyUsagePage";
import RateCardsPage from "@/pages/admin/RateCardsPage";
import IntegrationsPage from "@/pages/admin/IntegrationsPage";
import OAuthClientsPage from "@/pages/admin/OAuthClientsPage";
import ConsentPage from "@/pages/oauth/ConsentPage";
import MasterApiUsagePage from "@/pages/master/MasterApiUsagePage";
import MasterApiKeyUsagePage from "@/pages/master/MasterApiKeyUsagePage";
import WebhooksPage from "@/pages/admin/WebhooksPage";
import ImportsPage from "@/pages/admin/ImportsPage";
import ImportNewPage from "@/pages/admin/ImportNewPage";
import TimeImportsPage from "@/pages/admin/TimeImportsPage";
import ExportsPage from "@/pages/admin/ExportsPage";
import ExportProfilesPage from "@/pages/admin/ExportProfilesPage";
import ExportProfileEditPage from "@/pages/admin/ExportProfileEditPage";
import ExportRunPage from "@/pages/admin/ExportRunPage";
import WebhooksDocsPage from "@/pages/docs/WebhooksDocsPage";
import KioskPairPage from "@/pages/kiosk/KioskPairPage";
import KioskShell from "@/pages/kiosk/KioskShell";
import TasksPage from "@/pages/tasks/TasksPage";
import AdminTasksPage from "@/pages/tasks/AdminTasksPage";
import AdminRemindersPage from "@/pages/admin/AdminRemindersPage";
import RemindersPage from "@/pages/reminders/RemindersPage";
import SelectTenantPage from "@/pages/SelectTenantPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import PlatformShell from "@/pages/platform/PlatformShell";
import PlatformTenantsPage from "@/pages/platform/PlatformTenantsPage";
import PlatformAdminsPage from "@/pages/platform/PlatformAdminsPage";
import PlatformStatsPage from "@/pages/platform/PlatformStatsPage";
import PlatformImpersonationLogPage from "@/pages/platform/PlatformImpersonationLogPage";
import RunningTimeHUD from "@/components/RunningTimeHUD";
import { Toaster } from "@/components/ui/sonner";

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <LaunchCodeBootstrap>
          <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset/:token" element={<ResetPasswordPage />} />
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
            {/* Phase 22 — Set-password lands here from invite/grant emails */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Public magic-link landing */}
            <Route path="/approve" element={<MagicApprovePage />} />

            {/* Public kiosk pages — no auth boundary */}
            <Route path="/kiosk/pair" element={<KioskPairPage />} />
            <Route path="/kiosk" element={<KioskShell />} />

            {/* Public developer docs */}
            <Route path="/docs/webhooks" element={<WebhooksDocsPage />} />

            {/* Phase 17a — OAuth consent screen (requires auth, own layout) */}
            <Route
              path="/app/oauth/consent"
              element={
                <ProtectedRoute>
                  <ConsentPage />
                </ProtectedRoute>
              }
            />

            {/* Phase 22 — Tenant picker (post-login, own layout) */}
            <Route
              path="/app/select-tenant"
              element={
                <ProtectedRoute allowTempToken>
                  <SelectTenantPage />
                </ProtectedRoute>
              }
            />

            {/* Phase 22 — Platform console (requires platform_admin) */}
            <Route
              path="/app/platform"
              element={
                <ProtectedRoute requirePlatformAdmin>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route element={<PlatformShell />}>
                <Route index element={<Navigate to="tenants" replace />} />
                <Route path="tenants" element={<PlatformTenantsPage />} />
                <Route path="admins" element={<PlatformAdminsPage />} />
                <Route path="stats" element={<PlatformStatsPage />} />
                <Route path="impersonation-log" element={<PlatformImpersonationLogPage />} />
              </Route>
            </Route>

            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="pace" element={<PacePage />} />

              {/* My time */}
              <Route path="time" element={<ClockPage />} />
              <Route path="time/week" element={<WeekPage />} />
              <Route path="time/history" element={<HistoryPage />} />

              {/* Phase 21 — Scheduled tasks + reminders */}
              <Route path="tasks" element={<TasksPage />} />
              <Route path="reminders" element={<RemindersPage />} />
              <Route
                path="admin/tasks"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin", "approver"]}>
                    <AdminTasksPage />
                  </ProtectedRoute>
                }
              />

              {/* PTO */}
              <Route path="pto" element={<MyPtoPage />} />

              {/* Approvals */}
              <Route path="approvals" element={<PendingQueue />} />
              <Route path="approvals/history" element={<ApprovalsHistory />} />
              <Route path="approvals/pto" element={<PtoQueuePage />} />
              <Route path="approvals/:id" element={<TimesheetDetailPage />} />

              <Route
                path="directory"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin", "master"]}>
                    <DirectoryPage />
                  </ProtectedRoute>
                }
              />
              <Route path="settings/profile" element={<ProfilePage />} />
              <Route path="settings/notifications" element={<NotificationPreferencesPage />} />
              <Route
                path="admin/users"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/invites"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <AdminInvitesPage />
                  </ProtectedRoute>
                }
              />

              {/* Time Admin */}
              <Route
                path="admin/worksites"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <WorksitesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/worksites/:id"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <WorksiteDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/dimensions"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <DimensionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/dimensions/:id"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <DimensionDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/time-policy"
                element={
                  <ProtectedRoute roles={["tenant_admin"]}>
                    <TimePolicyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/time"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <AdminTimePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/approvals"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <AdminApprovalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/overtime-policy"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <OvertimePolicyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/break-policy"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <BreakPolicyPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pto-types"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <PtoTypesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pto-requests"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <PtoRequestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/checklists"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ChecklistsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/reminders"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <AdminRemindersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/kiosks"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <KiosksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/privacy"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <PrivacyNoticePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/api-keys"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ApiKeysPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/api-keys/:keyId/usage"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ApiKeyUsagePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/rates"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <RateCardsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/webhooks"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <WebhooksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/imports"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ImportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/imports/new"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ImportNewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/imports/:jobId"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ImportNewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/time-imports"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <TimeImportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/exports"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ExportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/exports/profiles"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ExportProfilesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/exports/profiles/new"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ExportProfileEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/exports/profiles/:profileId"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ExportProfileEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/exports/run"
                element={
                  <ProtectedRoute roles={["admin", "tenant_admin"]}>
                    <ExportRunPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/integrations"
                element={
                  <ProtectedRoute roles={["tenant_admin"]}>
                    <IntegrationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/oauth-clients"
                element={
                  <ProtectedRoute roles={["tenant_admin", "admin"]}>
                    <OAuthClientsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route
              path="/master"
              element={
                <ProtectedRoute roles={["master"]}>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/master/tenants" replace />} />
              <Route path="tenants" element={<MasterTenantsPage />} />
              <Route path="tenants/:tenant_id" element={<MasterWorkspaceDetailPage />} />
              <Route path="api-usage" element={<MasterApiUsagePage />} />
              <Route path="api-keys/:keyId/usage" element={<MasterApiKeyUsagePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
        </LaunchCodeBootstrap>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
